/**
 * Recipient delivery readiness — must mirror backend resolve_telegram_transport().
 */

import type { Recipient } from '@/types';
import { isNumericChatId, isPlainUsername, meRecipientWarning, type TelegramSenderMode } from '@/utils/telegramErrors';

export type RecipientReadinessInput = Pick<
  Recipient,
  | 'channel'
  | 'chat_id'
  | 'is_enabled'
  | 'delivery_provider'
  | 'source'
  | 'telegram_resolution_method'
>;

export type ReadinessTransport = 'telethon' | 'bot' | null;

export type RecipientReadinessContext = {
  botConfigured: boolean;
  telethonConfigured: boolean;
  telethonSessionReady: boolean;
  senderMode: TelegramSenderMode;
};

/** Primary transport that send routing will use (matches resolve_telegram_transport). */
export function resolveRecipientReadinessTransport(
  recipient: RecipientReadinessInput,
  ctx: RecipientReadinessContext,
): ReadinessTransport {
  if (recipient.channel !== 'telegram' || !recipient.is_enabled) {
    return null;
  }

  const provider = recipient.delivery_provider || 'auto';
  const source = recipient.source;

  if (provider === 'telethon') {
    return ctx.telethonConfigured ? 'telethon' : null;
  }
  if (provider === 'bot') {
    return ctx.botConfigured ? 'bot' : null;
  }

  // auto — mirror backend chain first hop
  if (source === 'bot') {
    return ctx.botConfigured ? 'bot' : null;
  }
  if (source === 'known_chat' || source === 'telethon') {
    return ctx.telethonConfigured && ctx.telethonSessionReady ? 'telethon' : null;
  }

  if (recipient.chat_id === 'me') {
    return ctx.telethonConfigured ? 'telethon' : null;
  }

  if (ctx.telethonConfigured && ctx.telethonSessionReady) {
    return 'telethon';
  }

  const method = recipient.telegram_resolution_method || '';
  if (method.startsWith('bot_') || method === 'numeric') {
    return ctx.botConfigured ? 'bot' : null;
  }
  if (isNumericChatId(recipient.chat_id)) {
    return ctx.botConfigured ? 'bot' : null;
  }
  if (isPlainUsername(recipient.chat_id || '')) {
    return null;
  }

  return ctx.botConfigured ? 'bot' : ctx.telethonConfigured ? 'telethon' : null;
}

export function readinessTitle(
  recipient: RecipientReadinessInput,
  transport: ReadinessTransport,
  ctx: RecipientReadinessContext,
): string {
  const provider = recipient.delivery_provider || 'auto';
  if (transport === 'telethon') {
    const via = recipient.telegram_resolution_method
      ? `Resolved via ${recipient.telegram_resolution_method} — `
      : '';
    return `${via}Deliveries use Telethon (delivery_provider=${provider})`;
  }
  if (transport === 'bot') {
    return `Deliveries use Bot API (delivery_provider=${provider})`;
  }
  if (recipient.chat_id === 'me') {
    return meRecipientWarning(ctx.senderMode);
  }
  if (isPlainUsername(recipient.chat_id || '')) {
    if (ctx.botConfigured) {
      return 'Ask them to open the bot and send /start, or switch delivery to Telethon.';
    }
    return 'Configure a bot token or Telethon session to deliver to usernames';
  }
  return 'Delivery readiness unknown';
}
